import { Form } from "@/components/ui/form";
import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsNew } from "@/features/FormCard/Actions";
import { DatePickerField, InputField } from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { useStrategyCurrency } from "./useStrategyCurrency";

const optionalNumberSchema = z.preprocess(
  (value) => (value === "" ? null : value),
  z.coerce.number().nullable().optional(),
);

const FormSchema = z.object({
  strategy_from_id: optionalNumberSchema,
  strategy_to_id: optionalNumberSchema,
  date: z.preprocess((val) => parseToDateValue(val), dateSchema),
  quantity: optionalNumberSchema,
  weight: optionalNumberSchema,
  market_value_local_ccy: optionalNumberSchema,
  market_value_ptf_ccy: optionalNumberSchema,
});

const formSchemaFields = FormSchema.keyof().enum;

export function S1PositionsNew() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    disable_translations_request: true,
    client: Clients.S1_QUANTILIA,
    table_name: Tables.S1_POSITIONS,
    FormSchema,
    isNew: true,
  });

  const strategyFromId = form.watch("strategy_from_id");
  const strategyToId = form.watch("strategy_to_id");
  const strategyFromCurrency = useStrategyCurrency(strategyFromId);
  const strategyToCurrency = useStrategyCurrency(strategyToId);

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${Tables.S1_POSITIONS}.new_position`, {
                  defaultValue: "New S1 position",
                })}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_from_id}
                    label={t(`${Tables.S1_POSITIONS}.columns.strategy_from_id`, {
                      ns: "tables",
                      defaultValue: "Strategy from ID",
                    })}
                    control={form.control}
                    endContent={
                      strategyFromCurrency ? (
                        <span className="text-xs text-default-400 whitespace-nowrap">
                          {strategyFromCurrency}
                        </span>
                      ) : undefined
                    }
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_to_id}
                    label={t(`${Tables.S1_POSITIONS}.columns.strategy_to_id`, {
                      ns: "tables",
                      defaultValue: "Strategy to ID",
                    })}
                    control={form.control}
                    endContent={
                      strategyToCurrency ? (
                        <span className="text-xs text-default-400 whitespace-nowrap">
                          {strategyToCurrency}
                        </span>
                      ) : undefined
                    }
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchemaFields.date}
                    label={t(`${Tables.S1_POSITIONS}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.quantity}
                    label={t(`${Tables.S1_POSITIONS}.columns.quantity`, {
                      ns: "tables",
                      defaultValue: "Quantity",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.weight}
                    label={t(`${Tables.S1_POSITIONS}.columns.weight`, {
                      ns: "tables",
                      defaultValue: "Weight",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.market_value_local_ccy}
                    label={t(
                      `${Tables.S1_POSITIONS}.columns.market_value_local_ccy`,
                      {
                        ns: "tables",
                        defaultValue: "Market value (local ccy)",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.market_value_ptf_ccy}
                    label={t(
                      `${Tables.S1_POSITIONS}.columns.market_value_ptf_ccy`,
                      {
                        ns: "tables",
                        defaultValue: "Market value (reporting ccy)",
                      },
                    )}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
        >
          {changedFields
            ? NewDiff(changedFields, [], {}, Tables.S1_POSITIONS)
            : t("no_changes")}
        </ConfirmationModal>

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
